import React, { useState, useContext, useEffect } from "react";
import styled from "@emotion/styled";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import DownloadIcon from "@mui/icons-material/Download";
import { DataContext } from "../../contexts/DataContext";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";

// Platform Preview Components
import FacebookPreview from "./preview/Facebook";
import InstagramPreview from "./preview/Instagram";
import YouTubePreview from "./preview/YouTube";
import { format_date } from "../../utils/helpers";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "2rem",
  padding: "2rem",
  minHeight: "calc(90vh - 200px)",
  background: "#FFFFFF",

  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
  },
}));

const LeftSection = styled("div")(({ theme }) => ({
  flex: "1",
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

const RightSection = styled("div")(({ theme }) => ({
  flex: "0 0 420px",
  display: "flex",
  flexDirection: "column",
  gap: "0rem",
  background: "red",
  borderRadius: "12px",
  border: `1px solid #E5E7EB`,
  overflow: "hidden",
  boxShadow: "rgba(0, 20, 67, 0.08) 3px 4px 4px inset",
  backgroundColor: "#eff4ff",

  [theme.breakpoints.down("lg")]: {
    flex: "1",
  },
}));

const FieldRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "1rem",

  "& .field_label": {
    width: "140px",
    fontSize: "14px",
    fontWeight: "500",
    color: theme.palette.text.secondary,
    paddingTop: "8px",
    flexShrink: 0,
  },

  "& .field_value": {
    flex: "1",
    fontSize: "14px",
    color: theme.palette.text.primary,
  },
  "& .field_value.read_only": {
    borderRadius: "4px",
    border: `1px solid ${theme.palette.divider}`,
    background: "#FFF",
    padding: "8px 4px 8px 12px",
  },
}));

const ChipContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "0.5rem",
  paddingTop: "4px",

  "& .MuiChip-root": {
    borderRadius: "20px",
    fontSize: "13px",
    height: "28px",
    backgroundColor: "#F3F4F6",
    color: theme.palette.text.primary,
    border: "none",

    "& .MuiChip-deleteIcon": {
      color: theme.palette.text.secondary,
      fontSize: "16px",
      "&:hover": {
        color: theme.palette.error.main,
      },
    },
  },
}));

const CaptionBox = styled("div")(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "4px",
  padding: "0",
  fontSize: "14px",
  lineHeight: "1.6",
  color: theme.palette.text.primary,
  whiteSpace: "pre-wrap",
}));

const PreviewHeader = styled("div")(({ theme }) => ({
  fontSize: "20px",
  fontWeight: "500",
  color: "rgb(37, 58, 106)",
  padding: "1rem",
}));

const PreviewCard = styled("div")(({ theme }) => ({
  padding: "0rem 1rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: "flex",
  gap: "0rem",
  background: "#F3F4F6",
  padding: "3px",
  borderRadius: "10px",
  border: "1px solid #E5E7EB",

  "& .MuiToggleButtonGroup-grouped": {
    border: "none",
    borderRadius: "8px !important",
    margin: "0",
  },

  "& .MuiToggleButton-root": {
    flex: 1,
    border: "none",
    textTransform: "none",
    fontSize: "14px",
    padding: "10px 16px",
    color: "#6B7280",
    fontWeight: "500",
    transition: "all 0.2s ease",

    "&.Mui-selected": {
      backgroundColor: "#FFFFFF",
      color: "#1F2937",
      fontWeight: "600",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",

      "&:hover": {
        backgroundColor: "#FFFFFF",
      },
    },

    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.6)",
    },
  },
}));

const ActionButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "0.75rem",
  marginTop: "0.5rem",
  paddingTop: "0.5rem",
  paddingBottom: "1rem",
}));

const ActionButton = styled("button")(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "12px 16px",
  background: "#FFFFFF",
  border: `1.5px solid #E5E7EB`,
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#374151",
  cursor: "pointer",
  transition: "all 0.2s ease",

  "&:hover:not(:disabled)": {
    backgroundColor: "#F9FAFB",
    borderColor: "#3B82F6",
    color: "#3B82F6",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  },

  "&:active:not(:disabled)": {
    transform: "translateY(0)",
  },

  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

const PlatformIcon = styled("span")(({ theme }) => ({
  fontSize: "14px",
  marginRight: "6px",
  display: "flex",
  alignItems: "center",
}));

const iconStyle = { fontSize: 20, marginRight: 0 };

const PLATFORM_CONFIGS = {
  facebook: {
    label: "Facebook",
    icon: <FacebookIcon sx={{ ...iconStyle, color: "#1877F2" }} />,
    component: FacebookPreview,
  },
  instagram: {
    label: "Instagram",
    icon: <InstagramIcon sx={{ ...iconStyle, color: "#E4405F" }} />,
    component: InstagramPreview,
  },
  youtube: {
    label: "YouTube",
    icon: <YouTubeIcon sx={{ ...iconStyle, color: "#FF0000" }} />,
    component: YouTubePreview,
  },
};

const BasicDetailsTab = ({ basicDetails: modalData }) => {
  const { formats } = useContext(DataContext);

  const [selectedPlatform, setSelectedPlatform] = useState("facebook");
  const [availablePlatforms, setAvailablePlatforms] = useState([]);

  const handlePlatformChange = (event, newPlatform) => {
    if (newPlatform !== null) {
      setSelectedPlatform(newPlatform);
    }
  };

  // Get the current platform's preview component
  const PreviewComponent =
    PLATFORM_CONFIGS[selectedPlatform]?.component || FacebookPreview;

  // Get current platform data
  const currentPlatformData =
    modalData?.posts_by_platform?.[selectedPlatform]?.[0] || {};

  // Determine if it's a video file
  const isVideoFile = (fileName) => {
    if (!fileName) return false;

    const cleanName = fileName.split(/[?#]/)[0].toLowerCase();

    return [".mp4", ".mov", ".avi", ".webm", ".mkv"].some((ext) =>
      cleanName.endsWith(ext),
    );
  };

  const firstFile = currentPlatformData.presigned_url ?? null;
  const fileUrl = currentPlatformData.presigned_url ?? null;
  const isVideo = isVideoFile(firstFile);

  // Handle download
  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = firstFile || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle post link
  const handlePostLink = () => {
    if (currentPlatformData?.post_link) {
      window.open(currentPlatformData.post_link, "_blank");
    }
  };

  useEffect(() => {
    // Get available platforms from posts_by_platform
    const availablePlatforms = modalData?.posts_by_platform
      ? Object.keys(modalData.posts_by_platform).filter(
          (platform) =>
            modalData.posts_by_platform[platform] &&
            modalData.posts_by_platform[platform].length > 0,
        )
      : [];
    if (availablePlatforms.length > 0) {
      setSelectedPlatform(availablePlatforms[0]);
    }
    setAvailablePlatforms(availablePlatforms);
  }, [modalData]);

  return (
    <>
      <Container>
        <LeftSection>
          <FieldRow>
            <div className="field_label">Format</div>
            <div className="field_value read_only">
              {modalData?.format_id
                ? (formats ?? []).find((f) => f.id === modalData.format_id)
                    ?.name || "N/A"
                : "N/A"}
            </div>
          </FieldRow>

          <FieldRow>
            <div className="field_label">Due Date</div>
            <div className="field_value read_only">
              {format_date(modalData?.due_at)}
            </div>
          </FieldRow>

          <FieldRow>
            <div className="field_label">Approver's</div>
            <ChipContainer className="field_value">
              {modalData?.approvers && modalData.approvers.length > 0 ? (
                modalData.approvers.map((approver) => (
                  <Chip
                    key={approver.id}
                    label={approver.name}
                    onDelete={() => {}}
                    deleteIcon={<CloseIcon />}
                  />
                ))
              ) : (
                <span style={{ paddingTop: "8px", color: "#9CA3AF" }}>
                  No approvers assigned
                </span>
              )}
            </ChipContainer>
          </FieldRow>

          <FieldRow>
            <div className="field_label">Graphic Designer</div>
            <ChipContainer className="field_value">
              {modalData?.designers && modalData.designers.length > 0 ? (
                modalData.designers.map((designer) => (
                  <Chip
                    sx={{ backgroundColor: "#ECF0FC" }}
                    key={designer.id}
                    label={designer.name}
                    onDelete={() => {}}
                    deleteIcon={<CloseIcon />}
                  />
                ))
              ) : (
                <span style={{ paddingTop: "8px", color: "#9CA3AF" }}>
                  No designers assigned
                </span>
              )}
            </ChipContainer>
          </FieldRow>

          <FieldRow>
            <div className="field_label">Content Writer</div>
            <ChipContainer className="field_value">
              {modalData?.content_writers &&
              modalData.content_writers.length > 0 ? (
                modalData.content_writers.map((writer) => (
                  <Chip
                    key={writer.id}
                    label={writer.name}
                    onDelete={() => {}}
                    deleteIcon={<CloseIcon />}
                    sx={{ backgroundColor: "#ECF0FC" }}
                  />
                ))
              ) : (
                <span style={{ paddingTop: "8px", color: "#9CA3AF" }}>
                  No content writers assigned
                </span>
              )}
            </ChipContainer>
          </FieldRow>

          <FieldRow>
            <div className="field_label">Caption</div>
            <div className="field_value read_only" style={{ width: "100%" }}>
              <CaptionBox>
                {currentPlatformData?.caption || modalData?.fall_back_caption || "No caption available"}
              </CaptionBox>
            </div>
          </FieldRow>
        </LeftSection>

        <RightSection>
          <PreviewHeader>Preview</PreviewHeader>

          <PreviewCard>
            {availablePlatforms.length > 1 && (
              <StyledToggleButtonGroup
                value={selectedPlatform}
                exclusive
                onChange={handlePlatformChange}
                aria-label="platform selection"
              >
                {availablePlatforms.map((platform) => (
                  <ToggleButton key={platform} value={platform}>
                    <PlatformIcon>
                      {PLATFORM_CONFIGS[platform]?.icon}
                    </PlatformIcon>
                    {PLATFORM_CONFIGS[platform]?.label}
                  </ToggleButton>
                ))}
              </StyledToggleButtonGroup>
            )}

            <PreviewComponent
              modalData={{
                ...modalData,
                caption: currentPlatformData?.caption,
                imageUrl: !isVideo ? fileUrl : null,
                videoUrl: isVideo ? fileUrl : null,
                postLink: currentPlatformData?.post_link,
              }}
              showStats={false}
            />

            <ActionButtons>
              <ActionButton
                onClick={handlePostLink}
                disabled={!currentPlatformData?.post_link}
              >
                <LinkIcon />
                Post Link
              </ActionButton>
              <ActionButton onClick={handleDownload} disabled={!fileUrl}>
                <DownloadIcon />
                Download
              </ActionButton>
            </ActionButtons>
          </PreviewCard>
        </RightSection>
      </Container>
    </>
  );
};

export default BasicDetailsTab;
